import {
  DefaultButton,
  IStackItemTokens,
  IStackProps,
  IStackTokens,
  Modal,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { v4 as uuidv4 } from "uuid";
import { AddItemFormInput, addItemFormsDefault } from "../../common/draftMenuHelper";
import { IMenuItemImage } from "./AddItemForm";
import getCroppedImage, { defaultCropOptions } from "./CropHelper";

type ImageCropFormProps = {
  control: Control<AddItemFormInput>;
  cropComplete: boolean;
  setCropImage: React.Dispatch<React.SetStateAction<IMenuItemImage>>;
  setCropComplete: () => void;
  onCancel: () => void;
  isModalOpen: boolean;
  closeModal: () => void;
};

export function ImageCropForm({
  control,
  cropComplete,
  setCropImage,
  setCropComplete,
  onCancel,
  isModalOpen,
  closeModal,
}: ImageCropFormProps) {
  const imageElementRef = useRef<HTMLImageElement | null | undefined>();
  const [crop, setCrop] = useState<Crop>(defaultCropOptions);
  const [croppedFile, setCroppedFile] = useState<File>();
  const [cropSubmitted, setCropSubmitted] = useState<boolean>(false);
  const [cropSuccessful, setCropSuccessful] = useState<boolean>(false);
  const image = useWatch({
    control,
    name: "image",
    defaultValue: addItemFormsDefault.image,
  });

  const resetState = () => {
    setCropSubmitted(false);
    setCropSuccessful(false);
    setCroppedFile(undefined);
  };

  useEffect(() => {
    if (cropSubmitted) {
      const cropFile = async () => {
        if (imageElementRef.current) {
          const croppedImage = await getCroppedImage(imageElementRef.current, crop, `${uuidv4()}.jpeg`);
          setCroppedFile(croppedImage);
          setCropSuccessful(true);
        }
      };
      cropFile();
    }
  }, [cropSubmitted, imageElementRef.current, crop]);

  useEffect(() => {
    if (cropSubmitted && cropSuccessful && croppedFile) {
      submitCroppedImage();
    }
  }, [cropSubmitted, cropSuccessful, croppedFile]);

  useEffect(() => {
    if (cropSubmitted && cropSuccessful && croppedFile) {
      resetState();
    }
  }, [cropSubmitted, cropSuccessful, croppedFile]);

  const onCloseModal = () => {
    resetState();
    onCancel();
    closeModal();
  };
  const submitCroppedImage = () => {
    if (croppedFile) {
      const croppedImage: IMenuItemImage = {
        imagePreviewUrl: URL.createObjectURL(croppedFile),
        file: croppedFile,
      };
      setCropImage(croppedImage);
      setCropComplete();
      setCropSubmitted(true);
      closeModal();
    }
  };

  const onCropSubmit = () => {
    setCropSubmitted(true);
  };

  const onLoad = useCallback((img: HTMLImageElement) => {
    imageElementRef.current = img;
    const width = crop.width;
    const height = crop.height;
    let x: number | undefined, y: number | undefined;
    if (crop.width) {
      x = img.width / 2 - crop.width / 2;
    }
    if (crop.height) {
      y = img.height / 2 - crop.height / 2;
    }
    const aspect = crop.aspect;
    if (x && y) {
      setCrop({
        unit: "px",
        width,
        height,
        x,
        y,
        aspect,
      });
    }

    return false;
  }, []);

  const stackToken: IStackTokens = {
    childrenGap: 10,
  };
  const stackItemTokens: IStackItemTokens = {
    padding: 5,
  };
  const rowProps: IStackProps = { horizontal: true, verticalAlign: "center" };

  return (
    // @ts-ignore
    <Modal isOpen={isModalOpen} onDismiss={closeModal} isBlocking={true}>
      {cropComplete ? (
        <></>
      ) : image?.previewUrl && !cropComplete && !cropSubmitted ? (
        <Stack tokens={stackToken} verticalFill={true}>
          {image.previewUrl && (
            <Stack.Item tokens={stackItemTokens}>
              <ReactCrop
                src={image.previewUrl}
                minWidth={defaultCropOptions.width}
                minHeight={defaultCropOptions.height}
                onImageLoaded={onLoad}
                crop={crop}
                onChange={(c) => setCrop(c)}
              />
            </Stack.Item>
          )}
          <Stack horizontalAlign="center">
            <Text aria-live="polite" aria-label="Cropped image dimension, width by height">
              {Math.floor(crop.width) + "px" + " by " + Math.floor(crop.height) + "px"}
            </Text>
          </Stack>

          <Stack horizontal={true} horizontalAlign="center">
            <Stack.Item tokens={stackItemTokens}>
              <DefaultButton onClick={onCropSubmit} text={"Submit"} />
            </Stack.Item>
            <Stack.Item tokens={stackItemTokens}>
              <DefaultButton onClick={onCloseModal} text={"Cancel"} />
            </Stack.Item>
          </Stack>
        </Stack>
      ) : cropSubmitted && !cropSuccessful ? (
        <Stack {...rowProps} tokens={stackToken}>
          <Spinner size={SpinnerSize.large} />
        </Stack>
      ) : (
        <></>
      )}
    </Modal>
  );
}
